import { useProjectStore } from '@/modules/identifier/state/use-project-store';
import { useGetAgentById, useUpdateWidget } from '@/modules/gpt-chats/hooks/use-agents';
import { useDeleteFile } from '@/lib/api/hooks/use-storage-file';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui-kit/sheet';
import { useTranslation } from 'react-i18next';
import { Chatbot } from '@/modules/gpt-chats/components/agent-integrations/widgets';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { useQueryState } from 'nuqs';
import { AgentIntegrationChatbotScript } from './agent-integration-chatbot-script';
import { AgentIntegrationChatbotDesign } from './agent-integration-chatbot-design';
import { FormProvider, useForm } from 'react-hook-form';
import { parseChatbotWidgetData } from '@/modules/gpt-chats/components/agent-integrations/widgets/chatbot/utils';
import { ChatbotWidget } from '@/modules/gpt-chats/components/agent-integrations/widgets/chatbot/index.type';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { WidgetType } from '@/modules/gpt-chats/models/widget.model';
import { AgentIntegrationGlobalSettings } from '../agent-integration-global-settings';
import { FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import useIsMobile from '@/hooks/use-is-mobile';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChatbotWidgetSchema } from './utils';
import { cn } from '@/lib/utils';
import { AgentIntegrationChatbotButtonDesign } from './agent-integrations-chatbot-button-design';

type AgentIntegrationsChatbotProps = {
  agentId: string;
};

export const AgentIntegrationsChatbot = ({ agentId }: AgentIntegrationsChatbotProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(true);
  const [panel, setPanel] = useQueryState('panel', { defaultValue: 'design' });
  const { tenantId } = useProjectStore().selectedProject || {
    tenantId: '',
  };
  const { data } = useGetAgentById(agentId);
  const form = useForm({
    values: parseChatbotWidgetData(data?.agent?.widget),
    resolver: zodResolver(ChatbotWidgetSchema),
    mode: 'all',
  });
  const { mutateAsync } = useUpdateWidget();
  const { mutateAsync: deleteFile } = useDeleteFile();
  const widget = form.watch();

  useEffect(() => {
    if (isMobile) return setOpen(false);
    setOpen(true);
  }, [isMobile]);

  if (!data) return null;
  const submitHandler = async (values: ChatbotWidget) => {
    try {
      const restSettings = data.agent.widget.widget_settings.filter(
        (item) => item.widget_type !== WidgetType.CHAT
      );
      const res = await mutateAsync({
        agent_id: agentId,
        // project_key: tenantId,
        regen_widget_id: false,
        widget: {
          ...data.agent.widget,
          name: values.name,
          logo_url: values.logo_url,
          greeting: values.greeting,
          site_url: values.site_url,
          brand_color: values.brand_color,
          widget_settings: [
            ...restSettings,
            {
              widget_type: WidgetType.CHAT,
              fe_style: JSON.stringify(values.designSettings),
            },
          ],
        },
      });
      if (!res.is_success) return showErrorToast({ errors: res.error });
      showSuccessToast({ description: t('CHATBOT_UPDATED_SUCCESSFULLY') });

      const oldSettings = parseChatbotWidgetData(data?.agent?.widget);
      const oldImageId = oldSettings?.designSettings?.banner?.imageId;
      const newImageId = values.designSettings?.banner?.imageId;

      if (oldImageId && oldImageId !== newImageId) {
        await deleteFile({
          fileId: oldImageId,
          projectKey: tenantId,
        });
      }
    } catch (_error) {
      showErrorToast({ errors: t('SOMETHING_WENT_WRONG') });
    }
  };

  return (
    <>
      <div
        className={cn(
          'flex w-[calc(100%-400px)] items-center justify-between',
          !isMobile && 'overflow-x-auto'
        )}
      >
        <div className="flex flex-1 items-center justify-center gap-6">
          <div className={cn('relative h-[550px] w-[400px] overflow-hidden bg-card p-6')}>
            <AgentIntegrationChatbotButtonDesign widget={widget} />
          </div>
          <div className="h-[550px] w-[400px] sm:w-[450px]">
            <Chatbot
              widgetId={data.agent.widget_id}
              agentId={agentId}
              widget={widget}
              // humanLogoUrl=""
            />
          </div>
        </div>
      </div>
      {isMobile && (
        <Button variant="outline" className="fixed right-0 top-1/2" onClick={() => setOpen(true)}>
          <FlaskConical className="h-4 w-4" />
        </Button>
      )}

      <Sheet
        open={open}
        modal={false}
        onOpenChange={(value) => {
          if (isMobile) setOpen(value);
        }}
      >
        <SheetContent className="agent-bot-integration top-[60px] flex h-[calc(100%-60px)] w-full flex-col">
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit(submitHandler)}
              className="flex h-full flex-col gap-6"
            >
              <SheetHeader>
                <SheetTitle>{t('SETTINGS')}</SheetTitle>
              </SheetHeader>
              <Tabs
                value={panel}
                onValueChange={setPanel}
                className="flex flex-1 flex-col gap-6 overflow-hidden"
              >
                <TabsList className="w-full">
                  <TabsTrigger value="design" className="flex-1">
                    {t('DESIGN')}
                  </TabsTrigger>
                  <TabsTrigger value="publish" className="flex-1">
                    {t('PUBLISH')}
                  </TabsTrigger>
                  <TabsTrigger value="global" className="flex-1">
                    {t('GLOBAL')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent
                  value="design"
                  className="flex-1 overflow-auto"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  <AgentIntegrationChatbotDesign />
                </TabsContent>
                <TabsContent value="publish" className="flex-1 overflow-auto">
                  <AgentIntegrationChatbotScript
                    widgetId={data.agent.widget_id}
                    projectKey={tenantId}
                    appDomainName={''}
                  />
                </TabsContent>
                <TabsContent
                  value="global"
                  className="flex-1 overflow-auto"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  <AgentIntegrationGlobalSettings />
                </TabsContent>
              </Tabs>
            </form>
          </FormProvider>
        </SheetContent>
      </Sheet>
    </>
  );
};
